import { gql } from "@apollo/client";

/**
 * GraphQL queries for AGENT role users.
 * These queries return only data owned by the logged-in agent.
 */

// Get agent's own listings (uses getUserCompanyListings which filters by logged-in user)
export const GET_AGENT_LISTINGS = gql`
  query getUserCompanyListings {
    getUserCompanyListings {
      _id
      acreage
      baths
      beds
      city
      company {
        _id
        companyName
      }
      user {
        id
        firstName
        lastName
        phone
        email
        licenseNo
      }
      amenities {
        _id
        title
      }
      office {
        _id
        name
      }
      streetAddr
      state
      sqFt
      price
      propertyType
      propertyCategory
      expiration
      lotSize
      yearBuilt
      zip
      notes
      createdAt
      coordinates
      imageURLs
      openHouse {
        startTime
        endTime
        date
        needHost
      }
      openHouses {
        startTime
        endTime
        needHost
      }
      links {
        title
        url
      }
    }
  }
`;

// Get agent's listings with their buyer matches
export const GET_AGENT_LISTING_MATCHES = gql`
  query getAllListingBuyerMatches {
    getAllListingBuyerMatches {
      _id
      streetAddr
      city
      state
      zip
      beds
      baths
      price
      sqFt
      propertyType
      expiration
      myBuyerMatches
      office {
        _id
        name
      }
    }
  }
`;

// Get buyer matches for a specific listing
export const GET_LISTING_BUYER_MATCHES = gql`
  query getListingBuyerMatches($listingId: String!) {
    getListingBuyerMatches(listingId: $listingId) {
      matchPercentage
      matchingCriteriaCount
      buyer {
        _id
        firstName
        lastName
        email
        phone
        user {
          id
          firstName
          lastName
          email
          phone
        }
      }
    }
  }
`;

// Get a single listing by ID (for editing and viewing)
export const GET_AGENT_LISTING_BY_ID = gql`
  query getListingById($listingId: String) {
    getListingById(listingId: $listingId) {
      _id
      expiration
      streetAddr
      city
      state
      zip
      beds
      baths
      yearBuilt
      price
      sqFt
      lotSize
      acreage
      buyer_commission
      propertyType
      propertyCategory
      company {
        _id
      }
      user {
        id
        firstName
        lastName
        email
        phone
      }
      notes
      createdAt
      deletedAt
      office {
        _id
      }
      amenities {
        _id
        title
      }
      coordinates
      imageURLs
      openHouse {
        startTime
        endTime
        date
        needHost
      }
      openHouses {
        startTime
        endTime
        needHost
      }
      links {
        title
        url
      }
    }
  }
`;

// Agent dashboard stats query - we'll create this on backend
export const GET_AGENT_DASHBOARD_STATS = gql`
  query getAgentDashboardStats {
    getAgentDashboardStats {
      totalListings
      activeListings
      totalBuyerMatches
    }
  }
`;

// Get agent's active buyers
export const GET_AGENT_BUYERS = gql`
  query getUserBuyers($userId: String!) {
    getUserBuyers(userId: $userId) {
      _id
      firstName
      lastName
      phone
      email
      notes
      isActive
      referral
      pre_qualified
      first_time_buyer
      fubId
      fubSource
      fubStage
      leadSource
      user {
        id
        firstName
        lastName
      }
      company {
        _id
        companyName
      }
      buyer_criteria {
        _id
        title
        minPrice
        maxPrice
        minBeds
        minBath
        minSqFt
        maxSqFt
        cities
        zipCodes
        propertyType
        notes
        optOut
        amenities {
          _id
          title
        }
        criteria_matches {
          _id
          listing {
            _id
            streetAddr
            city
            state
            zip
            price
            beds
            baths
            sqFt
            expiration
          }
        }
      }
    }
  }
`;

// Get agent's inactive buyers
export const GET_AGENT_INACTIVE_BUYERS = gql`
  query getUserUnActiveBuyers {
    getUserUnActiveBuyers {
      _id
      firstName
      lastName
      phone
      email
      notes
      isActive
      referral
      pre_qualified
      first_time_buyer
      fubId
      fubSource
      fubStage
      leadSource
      user {
        id
        firstName
        lastName
      }
      company {
        _id
        companyName
      }
      buyer_criteria {
        _id
        title
        minPrice
        maxPrice
        minBeds
        minBath
        minSqFt
        maxSqFt
        cities
        zipCodes
        propertyType
        notes
        optOut
        amenities {
          _id
          title
        }
        criteria_matches {
          _id
          listing {
            _id
            streetAddr
            city
            state
            zip
            price
            beds
            baths
            sqFt
            expiration
          }
        }
      }
    }
  }
`;

// Get buyer details with criteria and matches
export const GET_BUYER_DETAILS = gql`
  query getBuyerDetails($buyerId: String) {
    getBuyerDetails(buyerId: $buyerId) {
      _id
      firstName
      lastName
      phone
      email
      notes
      isActive
      referral
      pre_qualified
      first_time_buyer
      user {
        id
        firstName
        lastName
      }
      company {
        _id
      }
      buyer_criteria {
        _id
        minPrice
        maxPrice
        minBeds
        minBath
        minSqFt
        maxSqFt
        cities
        zipCodes
        propertyType
        amenities {
          _id
          title
        }
        criteria_matches {
          _id
          streetAddr
          city
          state
          zip
          price
          beds
          baths
          sqFt
          propertyType
          user {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;

// Add a new buyer
export const ADD_BUYER = gql`
  mutation addBuyer($buyerInput: BuyerInput) {
    addBuyer(buyerInput: $buyerInput) {
      _id
      firstName
      lastName
      phone
      email
      notes
      isActive
    }
  }
`;

// Update a buyer
export const UPDATE_BUYER = gql`
  mutation updateBuyer($buyerInput: BuyerInput) {
    updateBuyer(buyerInput: $buyerInput) {
      _id
      firstName
      lastName
      phone
      email
      notes
      isActive
    }
  }
`;

// Reset user password
export const RESET_USER_PASSWORD = gql`
  mutation resetUserPassword($currentPassword: String!, $password: String!) {
    resetUserPassword(currentPassword: $currentPassword, password: $password) {
      id
      email
    }
  }
`;

// Get all services for agent's company
export const GET_ALL_SERVICES = gql`
  query getAllServices($companyId: ID, $pagination: pagination, $searchText: String) {
    getAllServices(companyId: $companyId, pagination: $pagination, searchText: $searchText) {
      services {
        _id
        title
        icon
        createdAt
        updatedAt
      }
      totalCount
    }
  }
`;

// Get sub-companies by service ID
export const GET_SUB_COMPANIES_BY_SERVICE = gql`
  query getSubCompanyByServiceID($serviceId: String!, $pagination: pagination) {
    getSubCompanyByServiceID(serviceId: $serviceId, pagination: $pagination) {
      subCompanies {
        _id
        name
        address
        phone
        website
        serviceAreas
        notes
        createdAt
        updatedAt
      }
      totalCount
    }
  }
`;

// Get contacts by sub-company ID
export const GET_CONTACTS_BY_SUBCOMPANY = gql`
  query getContactsBySubCompany($subCompanyId: String!) {
    getContactsBySubCompany(subCompanyId: $subCompanyId) {
      _id
      name
      address
      phone
      url
      serviceAreas
      notes
      contacts {
        _id
        name
        position
        address
        email
        phone
        url
        createdAt
        updatedAt
      }
      service {
        _id
        title
      }
    }
  }
`;

// ==================== BUYER CRITERIA ====================

// Add buyer search criteria
export const ADD_CRITERIA = gql`
  mutation addCriteria($criteriaInput: CriteriaInput) {
    addCriteria(criteriaInput: $criteriaInput) {
      _id
      title
      minPrice
      maxPrice
      minBath
      minBeds
      minSqFt
      maxSqFt
      cities
      zipCodes
      propertyType
      notes
      optOut
      createdAt
      criteria_matches {
        _id
        streetAddr
        city
        state
        zip
        price
      }
    }
  }
`;

// Update buyer search criteria
export const UPDATE_CRITERIA = gql`
  mutation updateCriteria($criteriaInput: CriteriaInput) {
    updateCriteria(criteriaInput: $criteriaInput) {
      _id
      title
      minPrice
      maxPrice
      minBath
      minBeds
      minSqFt
      maxSqFt
      cities
      zipCodes
      propertyType
      notes
      optOut
      createdAt
      criteria_matches {
        _id
        streetAddr
        city
        state
        zip
        price
      }
    }
  }
`;

// Delete buyer search criteria
export const DELETE_CRITERIA = gql`
  mutation deleteCriteria($criteriaId: String) {
    deleteCriteria(criteriaId: $criteriaId) {
      _id
    }
  }
`;

// Get buyer criteria
export const GET_BUYER_CRITERIA = gql`
  query getBuyerCriteria($buyerId: String!) {
    getBuyerCriteria(buyerId: $buyerId) {
      _id
      title
      minPrice
      maxPrice
      minBath
      minBeds
      minSqFt
      maxSqFt
      cities
      zipCodes
      propertyType
      notes
      optOut
      createdAt
      criteria_matches {
        _id
        streetAddr
        city
        state
        zip
        price
        beds
        baths
        sqFt
        propertyType
      }
    }
  }
`;

// Get user notifications
export const GET_USER_NOTIFICATIONS = gql`
  query getUserNotifications {
    getUserNotifications {
      _id
      title
      body
      read
      type
      createdAt
      listing {
        _id
        streetAddr
        city
        state
        zip
        price
        user {
          firstName
          lastName
        }
      }
    }
  }
`;

// Mark notifications as read
export const MARK_NOTIFICATIONS_READ = gql`
  mutation markNotificationAsReadByIds($notificationIds: [String!]) {
    markNotificationAsReadByIds(notificationIds: $notificationIds)
  }
`;

// ==================== WHITEBOARD QUERIES ====================

// Get all company listings with filtering, sorting, pagination (Whiteboard)
export const GET_WHITEBOARD_LISTINGS = gql`
  query getCompanyListings(
    $companyId: String!
    $listingInput: FilteringListingInput
    $pagination: pagination
    $searchText: String
    $sortBy: String
  ) {
    getCompanyListings(
      companyId: $companyId
      listingInput: $listingInput
      pagination: $pagination
      searchText: $searchText
      sortBy: $sortBy
    ) {
      listing {
        _id
        streetAddr
        city
        state
        zip
        beds
        baths
        price
        sqFt
        lotSize
        yearBuilt
        acreage
        propertyType
        expiration
        createdAt
        updatedAt
        buyer_commission
        coordinates
        imageURLs
        notes
        company {
          _id
          companyName
        }
        user {
          id
          firstName
          lastName
          phone
          email
        }
        office {
          _id
          name
        }
        amenities {
          _id
          title
        }
        openHouses {
          startTime
          endTime
          needHost
        }
        links {
          title
          url
        }
      }
      totalCount
    }
  }
`;

// Get company listings with open houses
export const GET_WHITEBOARD_OPEN_HOUSES = gql`
  query getCompanyOpenHouseListings(
    $companyId: String!
    $listingInput: FilteringListingInput
    $sortBy: String
  ) {
    getCompanyOpenHouseListings(
      companyId: $companyId
      listingInput: $listingInput
      sortBy: $sortBy
    ) {
      _id
      streetAddr
      city
      state
      zip
      beds
      baths
      price
      sqFt
      expiration
      coordinates
      imageURLs
      user {
        id
        firstName
        lastName
        phone
        email
      }
      office {
        _id
        name
      }
      openHouses {
        startTime
        endTime
        needHost
      }
    }
  }
`;

// Get buyer wants & needs (criteria) for company
// Uses same fields as mobile app: buyerId and agentName are returned directly from server
export const GET_WHITEBOARD_WANTS_NEEDS = gql`
  query getCriteriaListingByCompany(
    $companyId: String!
    $search: String
    $sortBy: String
  ) {
    getCriteriaListingByCompany(
      companyId: $companyId
      search: $search
      sortBy: $sortBy
    ) {
      _id
      title
      minPrice
      maxPrice
      minBeds
      minBath
      minSqFt
      maxSqFt
      cities
      zipCodes
      propertyType
      notes
      createdAt
      buyerId
      agentName
      agentPhone
      agentEmail
      notes
      amenities {
        _id
        title
      }
    }
  }
`;

